<template>
  <q-layout view="hHh lpR fFf">
    <a class="skip-link" href="#main">Skip to content</a>
    <!-- No bg-dark/text-white here: both are fixed colours that survive a
         scheme switch and would put white text on a white bar. -->
    <q-header class="shell-header">
      <q-toolbar class="shell-toolbar">
        <!-- Below 1024px seven tabs do not fit; they move into a drawer. -->
        <q-btn
          v-if="boardReady"
          flat
          dense
          round
          icon="menu"
          class="lt-md q-mr-xs"
          aria-label="Open navigation"
          @click="navOpen = true"
        />

        <router-link to="/mrs" class="shell-brand">
          <span class="shell-mark" aria-hidden="true" />
          CTF Workflow
        </router-link>

        <!-- Grouped by the job, not by build order: merge-request work,
             planning, knowledge. A thin rule separates the groups. -->
        <q-tabs
          v-if="boardReady"
          dense
          shrink
          no-caps
          inline-label
          align="left"
          role="navigation"
          aria-label="Main"
          active-color="primary"
          indicator-color="primary"
          class="shell-tabs gt-sm"
        >
          <template v-for="(group, gi) in NAV_GROUPS" :key="group.name">
            <span v-if="gi > 0" class="shell-tab-sep" aria-hidden="true" />
            <q-route-tab
              v-for="item in group.items"
              :key="item.to"
              :to="item.to"
              :exact="item.exact"
              :icon="item.icon"
              :label="item.label"
              :title="item.hint"
            >
              <!-- Inline, not floating: with inline labels a floating badge
                   sits on the header's top edge and is half cut off. -->
              <q-badge
                v-if="item.to === '/palace' && pending > 0"
                color="warning"
                class="q-ml-xs"
                :aria-label="`${pending} waiting for a decision`"
              >
                {{ pending }}
              </q-badge>
            </q-route-tab>
          </template>
        </q-tabs>

        <q-space />

        <q-btn
          flat
          dense
          round
          size="sm"
          :icon="schemeIcon"
          :aria-label="`Colour scheme: ${schemeLabel}`"
          :title="`Colour scheme: ${schemeLabel}`"
        >
          <!-- A native title, not q-tooltip: the tooltip stayed on top of the
               menu it opens. -->
          <q-menu anchor="bottom right" self="top right">
            <q-list dense style="min-width: 140px">
              <q-item
                v-for="option in SCHEME_OPTIONS"
                :key="option.value"
                v-close-popup
                clickable
                :active="scheme === option.value"
                active-class="text-primary"
                @click="setScheme(option.value)"
              >
                <q-item-section avatar>
                  <q-icon :name="option.icon" size="18px" />
                </q-item-section>
                <q-item-section>{{ option.label }}</q-item-section>
              </q-item>
            </q-list>
          </q-menu>
        </q-btn>

        <q-btn
          v-if="boardReady"
          flat
          dense
          round
          size="sm"
          class="q-ml-xs"
          icon="settings"
          aria-label="Settings"
          to="/settings"
        >
          <q-tooltip>Settings: access, API tokens, health</q-tooltip>
        </q-btn>

        <SignInMenu />
      </q-toolbar>
    </q-header>

    <q-drawer
      v-if="boardReady"
      v-model="navOpen"
      side="left"
      overlay
      behavior="mobile"
      bordered
      :width="280"
      class="shell-drawer"
    >
      <q-list padding role="navigation" aria-label="Main, in the drawer">
        <template v-for="group in NAV_GROUPS" :key="group.name">
          <q-item-label header class="shell-drawer-head">
            {{ group.name }}
          </q-item-label>
          <q-item
            v-for="item in group.items"
            :key="item.to"
            clickable
            :to="item.to"
            :exact="item.exact"
            active-class="shell-drawer-active"
            @click="navOpen = false"
          >
            <q-item-section avatar>
              <q-icon :name="item.icon" size="20px" />
            </q-item-section>
            <q-item-section>
              <q-item-label>{{ item.label }}</q-item-label>
              <q-item-label caption>{{ item.hint }}</q-item-label>
            </q-item-section>
            <q-item-section v-if="item.to === '/palace' && pending > 0" side>
              <q-badge color="warning">{{ pending }}</q-badge>
            </q-item-section>
          </q-item>
        </template>
      </q-list>
    </q-drawer>

    <q-page-container id="main">
      <q-page
        v-if="!resolved"
        class="column no-wrap items-center justify-center"
      >
        <q-spinner color="primary" size="32px" />
      </q-page>
      <SignInGate v-else-if="mustSignIn" />
      <router-view v-else />
    </q-page-container>
  </q-layout>
</template>

<script lang="ts" setup>
import { useQuery } from "@tanstack/vue-query";
import { computed, ref } from "vue";
import { RouterView } from "vue-router";

import { fetchCandidates } from "@/api/client";
import SignInGate from "@/components/SignInGate.vue";
import SignInMenu from "@/components/SignInMenu.vue";
import { useColorScheme, type ColorScheme } from "@/composables/useColorScheme";
import { useCurrentUser } from "@/composables/useCurrentUser";

/**
 * The badge shares its query key with the palace inbox, so vue-query serves
 * both from one request and a promotion updates the badge without extra work.
 * It reads `total`, not the row count — the list is capped server-side.
 */
const { user, resolved, methods, loadCurrentUser } = useCurrentUser();
loadCurrentUser();

const mustSignIn = computed(
  () => resolved.value && methods.value.required && user.value === null,
);
const boardReady = computed(
  () => resolved.value && (!methods.value.required || user.value !== null),
);

const { data } = useQuery({
  queryKey: ["palace", { status: "pending" }],
  queryFn: () => fetchCandidates({ status: "pending" }),
  enabled: boardReady,
});

const pending = computed(() => data.value?.total ?? 0);

const navOpen = ref(false);

type NavItem = {
  to: string;
  label: string;
  icon: string;
  /** What the page is for, in words a first-time visitor understands. */
  hint: string;
  exact?: boolean;
};

/**
 * One list feeds both the tabs and the drawer, so the two can never disagree.
 * Routes are unchanged; only the order and grouping are.
 */
const NAV_GROUPS: { name: string; items: NavItem[] }[] = [
  {
    name: "Merge requests",
    items: [
      {
        to: "/mrs",
        label: "MRs",
        icon: "merge",
        hint: "Open merge requests: approvals, pipelines, your queue",
      },
      {
        to: "/releases",
        label: "Releases",
        icon: "flag",
        hint: "Milestones and what is still open in each",
      },
      {
        to: "/sandboxes",
        label: "Sandboxes",
        icon: "cloud",
        hint: "Feature environments and whether they respond",
      },
    ],
  },
  {
    name: "Planning",
    items: [
      {
        to: "/sprint",
        label: "Sprint",
        icon: "view_kanban",
        hint: "Team sprint boards from Jira",
      },
      {
        to: "/check",
        label: "Check",
        icon: "fact_check",
        hint: "Is a ticket complete enough to start?",
      },
    ],
  },
  {
    name: "Knowledge",
    items: [
      {
        to: "/",
        exact: true,
        label: "Knowledge",
        icon: "search",
        hint: "Search and ask across docs, Jira and MRs",
      },
      {
        to: "/palace",
        label: "Palace",
        icon: "inbox",
        hint: "Knowledge the review agent proposed, waiting for a decision",
      },
    ],
  },
];

const SCHEME_OPTIONS: {
  value: ColorScheme;
  label: string;
  icon: string;
}[] = [
  { value: "light", label: "Light", icon: "light_mode" },
  { value: "dark", label: "Dark", icon: "dark_mode" },
  { value: "system", label: "System", icon: "brightness_medium" },
];

const { scheme, setScheme } = useColorScheme();

const schemeOption = computed(
  () =>
    SCHEME_OPTIONS.find((o) => o.value === scheme.value) ?? SCHEME_OPTIONS[2]!,
);
const schemeIcon = computed(() => schemeOption.value.icon);
const schemeLabel = computed(() => schemeOption.value.label);
</script>

<style lang="sass" scoped>
.shell-header
  background: var(--bg-1) !important
  color: var(--text)
  border-bottom: 1px solid var(--border)
  height: var(--header-h)

.shell-toolbar
  min-height: var(--header-h)
  padding: 0 12px
  gap: 4px

.shell-brand
  display: flex
  align-items: center
  gap: 8px
  margin-right: 16px
  font-size: 14px
  font-weight: 700
  letter-spacing: -0.02em
  white-space: nowrap
  color: var(--text)
  text-decoration: none
  border-radius: var(--radius-sm)
  &:focus-visible
    outline: 2px solid var(--focus)
    outline-offset: 4px

.shell-mark
  width: 8px
  height: 8px
  border-radius: 2px
  background: var(--q-primary)
  flex: 0 0 auto

.shell-tabs
  flex: 0 1 auto
  min-width: 0
  \:deep(.q-tab)
    padding: 0 10px
    min-height: var(--header-h)
  \:deep(.q-tab__label)
    font-size: 13px
    font-weight: 500
  \:deep(.q-tab__icon)
    font-size: 18px
  // Between 1024 and 1279 the labels carry the meaning; the icons are what
  // gets dropped, because seven bare icons are a guessing game.
  @media (max-width: 1279px)
    \:deep(.q-tab__icon)
      display: none
    \:deep(.q-tab)
      padding: 0 8px

.shell-tab-sep
  align-self: center
  width: 1px
  height: 20px
  margin: 0 6px
  background: var(--border-strong)
  flex: 0 0 auto

.shell-drawer-head
  font-size: var(--type-caption)
  font-weight: 600
  color: var(--text-muted)
  padding-bottom: 4px

.shell-drawer-active
  color: var(--q-primary)
  background: var(--bg-2)
</style>
